import React, { useState } from "react";
import { useLocation } from "react-router-dom";

const Settings = () => {
  const location = useLocation();
  const initialTab = location.state?.activeTab || "profile";

  const [activeTab, setActiveTab] = useState(initialTab);
  const [profile, setProfile] = useState({
    name: "John Doe",
    email: "john.doe@example.com",
    company: "Example Corp",
    phone: "(555) 123-4567",
  });

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    smsNotifications: false,
    marketingEmails: true,
    eventReminders: true,
  });

  const [subscription] = useState({
    plan: "Free",
    status: "Active",
    renewalDate: "2024-12-31",
  });

  const tabs = [
    { id: "profile", label: "Profile", icon: "👤" },
    { id: "notifications", label: "Notifications", icon: "🔔" },
    { id: "subscription", label: "Subscription", icon: "💳" },
    { id: "privacy", label: "Privacy", icon: "🔒" },
  ];

  const handleProfileUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Profile updated successfully!");
  };

  const handleNotificationChange = (key: string) => {
    setNotifications((prev) => ({
      ...prev,
      [key]: !prev[key as keyof typeof prev],
    }));
  };

  const handleUpgrade = () => {
    alert("Upgrade feature coming soon! Contact us for premium options.");
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Settings</h1>
          <p className="text-gray-600">
            Manage your account preferences and settings
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-64">
            <div className="bg-white rounded-lg shadow-md">
              <nav className="p-4">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? "bg-[#9b87f5] text-white"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <span className="text-lg">{tab.icon}</span>
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-md p-6">
              {/* Profile Tab */}
              {activeTab === "profile" && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    Profile Information
                  </h2>
                  <form onSubmit={handleProfileUpdate} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={profile.name}
                          onChange={(e) =>
                            setProfile((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9b87f5] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={profile.email}
                          onChange={(e) =>
                            setProfile((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9b87f5] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Company
                        </label>
                        <input
                          type="text"
                          value={profile.company}
                          onChange={(e) =>
                            setProfile((prev) => ({
                              ...prev,
                              company: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9b87f5] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={profile.phone}
                          onChange={(e) =>
                            setProfile((prev) => ({
                              ...prev,
                              phone: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9b87f5] focus:border-transparent"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="bg-[#9b87f5] hover:bg-[#8b77e5] text-white px-6 py-2 rounded-lg font-medium transition-colors"
                    >
                      Update Profile
                    </button>
                  </form>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === "notifications" && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    Notification Preferences
                  </h2>
                  <div className="space-y-6">
                    {Object.entries(notifications).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between py-3"
                      >
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {key
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase())}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {key === "emailNotifications" &&
                              "Receive important updates via email"}
                            {key === "smsNotifications" &&
                              "Get SMS alerts for urgent matters"}
                            {key === "marketingEmails" &&
                              "Stay informed about new features and promotions"}
                            {key === "eventReminders" &&
                              "Get reminders about upcoming events and workshops"}
                          </p>
                        </div>
                        <button
                          onClick={() => handleNotificationChange(key)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            value ? "bg-[#9b87f5]" : "bg-gray-200"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              value ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Subscription Tab */}
              {activeTab === "subscription" && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    Subscription Details
                  </h2>
                  <div className="space-y-6">
                    <div className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            Current Plan
                          </h3>
                          <p className="text-gray-600">
                            {subscription.plan} Plan
                          </p>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            subscription.status === "Active"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {subscription.status}
                        </span>
                      </div>
                      <div className="mb-6">
                        <p className="text-sm text-gray-600">
                          {subscription.plan === "Free"
                            ? "Access to basic AI tools and features"
                            : `Renewal Date: ${subscription.renewalDate}`}
                        </p>
                      </div>
                      {subscription.plan === "Free" && (
                        <button
                          onClick={handleUpgrade}
                          className="bg-[#9b87f5] hover:bg-[#8b77e5] text-white px-6 py-2 rounded-lg font-medium transition-colors"
                        >
                          Upgrade to Premium
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="border border-gray-200 rounded-lg p-6">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Free Plan
                        </h4>
                        <p className="text-gray-600 text-sm mb-4">
                          Perfect for getting started
                        </p>
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li>✓ Basic AI tools</li>
                          <li>✓ 5 automations per month</li>
                          <li>✓ Email support</li>
                        </ul>
                      </div>
                      <div className="border border-[#9b87f5] rounded-lg p-6">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Premium Plan
                        </h4>
                        <p className="text-gray-600 text-sm mb-4">
                          For power users and teams
                        </p>
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li>✓ All AI tools</li>
                          <li>✓ Unlimited automations</li>
                          <li>✓ Priority support</li>
                          <li>✓ Custom integrations</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Privacy Tab */}
              {activeTab === "privacy" && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    Privacy & Security
                  </h2>
                  <div className="space-y-6">
                    <div className="border border-gray-200 rounded-lg p-6">
                      <h3 className="font-medium text-gray-900 mb-2">
                        Data Privacy
                      </h3>
                      <p className="text-gray-600 text-sm mb-4">
                        We take your privacy seriously. Your data is encrypted
                        and never shared with third parties without your
                        consent.
                      </p>
                      <button className="text-[#9b87f5] hover:text-[#8b77e5] font-medium">
                        View Privacy Policy
                      </button>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-6">
                      <h3 className="font-medium text-gray-900 mb-2">
                        Account Security
                      </h3>
                      <p className="text-gray-600 text-sm mb-4">
                        Manage your account security settings and two-factor
                        authentication.
                      </p>
                      <button className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors">
                        Change Password
                      </button>
                    </div>

                    <div className="border border-red-200 rounded-lg p-6">
                      <h3 className="font-medium text-red-900 mb-2">
                        Delete Account
                      </h3>
                      <p className="text-red-600 text-sm mb-4">
                        Permanently delete your account and all associated data.
                        This action cannot be undone.
                      </p>
                      <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                        Delete Account
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
